import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { useToast } from '../../context/ToastContext';
import { PosSaleSummary, PosSaleStatus, PosPostingStatus } from '../../interfaces/pos.interface';
import { formatMoney } from '../../utils/currency';
import { formatDateTime } from '../../utils/dateUtils';

// Same chip registers as PosSaleList.tsx / PosSaleDetail.tsx -- kept identical rather than
// re-derived, since a VOIDED/REVERSAL_FAILED row genuinely is the "needs attention" register,
// never the "something is broken with the sale" one (spec: a successful void that left the
// ledger reversal unresolved is not a failed void).
const SALE_STATUS_COLORS: Record<PosSaleStatus, 'success' | 'default'> = {
    COMPLETED: 'success',
    VOIDED: 'default',
};

const POSTING_STATUS_COLORS: Record<PosPostingStatus, 'success' | 'default' | 'warning' | 'error'> = {
    PENDING: 'warning',
    POSTED: 'success',
    FAILED: 'error',
    SKIPPED: 'default',
    REVERSAL_FAILED: 'warning',
};

// A row is only ever one of these three statuses in this queue (spec §7) -- PosSaleQueryController
// deliberately excludes SKIPPED (a clean void with nothing left to do) and POSTED (nothing owed).
const isReversalOfAVoid = (row: PosSaleSummary): boolean =>
    row.status === 'VOIDED' && row.postingStatus === 'REVERSAL_FAILED';

// Fix round 1 (F-1): "Post all visible" posts at most this many sales at once. Each POST opens
// its own REQUIRES_NEW transaction with a SELECT ... FOR UPDATE lock and a GL post -- a page of
// 100 rows firing 100 simultaneous requests saturates the JDBC pool and the HTTP connection cap.
// This is a background-ish maintenance action (an operator clearing a backlog after a
// chart-of-accounts outage), not a latency-sensitive one, so a small bounded concurrency is the
// right trade.
const POST_ALL_CONCURRENCY = 5;

const PosUnpostedQueue: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<PosSaleSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    // Distinguishes "the queue is genuinely empty" from "the load failed" (final-review F1,
    // same defect as PosCashierAccess.tsx's F1 -- see the comment at PosCashierAccess.tsx:97).
    // This is the one screen whose entire job is to nag about unposted sales, so a failed load
    // rendering "Nothing is waiting to post." is the worst possible false all-clear.
    const [loadError, setLoadError] = useState(false);

    // Ids currently being posted -- disables that row's own button without freezing the whole
    // grid, and lets "Post all visible" show progress per row rather than one opaque spinner.
    const [postingIds, setPostingIds] = useState<Set<string>>(new Set());
    const [postingAll, setPostingAll] = useState(false);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size]);

    const load = async () => {
        try {
            setLoading(true);
            setLoadError(false);
            const response = await PosService.listUnposted(page, size);
            setRows(response.content);
            setTotal(response.totalElements);
        } catch (err) {
            setLoadError(true);
            toast.showApiError(err, 'Failed to load the unposted sales queue');
        } finally {
            setLoading(false);
        }
    };

    // Posting is retryable by design (spec §7) -- pressing this twice, or pressing it on a row
    // that a scheduled sweep already cleared underneath the operator, must never be treated as an
    // error. Always reloads the page afterwards rather than patching the row in place: a "post
    // now" on a REVERSAL_FAILED row can change EITHER postingStatus (retry failed again) OR both
    // status/postingStatus semantics stay the same but postingAttempts/postingError move, and a
    // full reload is the one place that is guaranteed correct for every one of those outcomes.
    const handlePostNow = async (id: string) => {
        setPostingIds(prev => new Set(prev).add(id));
        try {
            await PosService.postNow(id);
            await load();
        } catch (err) {
            toast.showApiError(err, 'Failed to post this sale');
        } finally {
            setPostingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    // "Post all visible": exactly what it says -- only the rows on the CURRENT page, never every
    // unposted sale across every page. Fix round 1 (F-1): a naive Promise.all over every visible
    // id fired up to rowsPerPageOptions' max (100) simultaneous POSTs, each opening its own
    // REQUIRES_NEW transaction with a SELECT ... FOR UPDATE lock -- connection-pool saturation on
    // the server, and a stack of 100 error toasts on the client for an action that mostly
    // succeeded. Posting a small CHUNK at a time bounds the concurrency; a single summary toast
    // afterwards ("N posted, M could not post") reports the whole outcome once, since a partial
    // result here is a normal, expected outcome of a retryable action -- never an error state.
    const handlePostAllVisible = async () => {
        setPostingAll(true);
        const ids = rows.map(r => r.id);
        let succeeded = 0;
        let failed = 0;
        try {
            for (let i = 0; i < ids.length; i += POST_ALL_CONCURRENCY) {
                const chunk = ids.slice(i, i + POST_ALL_CONCURRENCY);
                const outcomes = await Promise.allSettled(chunk.map(id => PosService.postNow(id)));
                outcomes.forEach(outcome => {
                    if (outcome.status === 'fulfilled') succeeded += 1;
                    else failed += 1;
                });
            }
        } finally {
            setPostingAll(false);
            // Fix round 1 (F-3): a bulk post is exactly the action that can empty the CURRENT
            // page (or leave it out of range), so this always returns to page 0 rather than
            // reloading whatever page the operator happened to be on. Exactly ONE of these two
            // branches runs, so there is never a duplicate load racing the pagination state:
            // if already on page 0, `setPage(0)` would be a no-op (the `[page, size]` effect
            // would never re-fire), so `load()` is called directly here instead; otherwise
            // `setPage(0)` itself is what triggers that effect's own load for page 0 -- the same
            // mechanism `onPageChange`/`onRowsPerPageChange` already rely on below.
            if (page === 0) {
                await load();
            } else {
                setPage(0);
            }
            if (failed === 0) {
                toast.showSuccess(`${succeeded} sale${succeeded === 1 ? '' : 's'} posted.`);
            } else {
                toast.showError(
                    `${succeeded} sale${succeeded === 1 ? '' : 's'} posted, ${failed} could not post yet.`,
                );
            }
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h5">Unposted Sales</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every one of these sales already rang, printed and was paid for at the
                        counter -- only its ledger entry has not landed yet.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    onClick={handlePostAllVisible}
                    disabled={postingAll || rows.length === 0 || loading}
                >
                    Post all visible
                </Button>
            </Box>

            {loadError ? (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={load}>
                            Retry
                        </Button>
                    )}
                >
                    Failed to load the unposted sales queue. This is NOT the same as there being
                    nothing to post -- try again before assuming the queue is empty.
                </Alert>
            ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Receipt</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell>Sold At</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Posting</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8}>
                                        <Typography color="text.secondary">
                                            Nothing is waiting to post.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map(row => (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        <RouterLink to={`/pos/sales/${row.id}`}>{row.receiptNo}</RouterLink>
                                    </TableCell>
                                    <TableCell>{row.storeName ?? row.storeId}</TableCell>
                                    <TableCell>{formatDateTime(row.soldAt)}</TableCell>
                                    <TableCell align="right">{formatMoney(row.totalAmount)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" color={SALE_STATUS_COLORS[row.status]} label={row.status} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={POSTING_STATUS_COLORS[row.postingStatus]}
                                            label={row.postingStatus}
                                        />
                                        {/* The one row whose meaning is not obvious: a VOIDED sale here means
                                            the REVERSAL failed, not the original posting -- explained inline
                                            so an operator does not go and void the sale a second time.
                                            Fix round 1 (F-4): the original wording asserted "stock was already
                                            returned" unconditionally, but PosSaleQueryService#voidSale only
                                            reverses stock when stockApplied was true -- PosSaleSummaryDTO does
                                            not carry that flag, so this is worded to be true either way rather
                                            than adding a field just to branch on it. The part that actually
                                            stops a second void ("the sale IS voided") is unchanged. */}
                                        {isReversalOfAVoid(row) && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                This is the reversal of a voided sale, not the sale itself. The
                                                sale is voided; only the ledger reversal is outstanding. Post
                                                now retries it.
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}>
                                        {/* Truncated to 1024 chars server-side already -- this is the
                                            operator's only diagnostic, so it is shown in FULL here, never
                                            clipped further (no noWrap/ellipsis). */}
                                        {row.postingError ? (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {row.postingError}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handlePostNow(row.id)}
                                            disabled={postingIds.has(row.id) || postingAll}
                                        >
                                            Post now
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default PosUnpostedQueue;
